import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { isMobile, isTablet } from 'react-device-detect'

import { clsx } from 'clsx'
import { useAtomValue, useSetAtom } from 'jotai'

import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'

import RowSkeleton from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/(skeleton)/RowSkeleton'
import { Collapse } from '@/components/shared/Collapse'
import Price from '@/components/shared/Price'
import { DepartureFunctions } from '@/components/yourbooking/YourBookingHandlers'
import { departuresBusyAtom, loadingAtom } from '@/states/loadingAtoms'
import { SelectedDepartures } from '@/states/selection.interface'
import { selectedDateAtom, selectedDeparturesAtom, selectedOfferAtom } from '@/states/selectionAtom'
import ChevronDownIcon from '@/ui-cvi/components/dxp-icons/system/ChevronDownIcon'
import ChevronUpIcon from '@/ui-cvi/components/dxp-icons/system/ChevronUpIcon'
import StatusSuccessFilledIcon from '@/ui-cvi/components/dxp-icons/system/StatusSuccessFilled'

import { useTranslation } from '@/hooks/useDebugTranslations'

import { RouteIllustration } from './DepartureRow'
import DepartureRowSelectionSingleTier from './DepartureRowSelectionSingleTier'
import { DepartureWithNoOfferModal } from './DepartureWithNoOfferModal'

const isDepartureOverlapping = (
  returnLeg: boolean | undefined,
  selectedDepartures: SelectedDepartures,
  checkinTime: string,
  arrivalTime: string
): boolean => {
  if (returnLeg) {
    const selectedArrivalTime = selectedDepartures?.departureOut?.arrivalDateTime
    if (!selectedArrivalTime) return false
    if (selectedArrivalTime && selectedArrivalTime < checkinTime) return false
  } else {
    const selectedCheckinTime = selectedDepartures?.departureHome?.checkinDateTime
    if (!selectedCheckinTime) return false
    if (selectedCheckinTime && selectedCheckinTime > arrivalTime) return false
  }
  return true
}

interface DepartureRowJerseyProps {
  accordionOpen: boolean
  active: boolean
  arrivalDateTime: string
  arrivalPortCode: string
  arrivalPortName?: string
  arrivalTime: string
  checkinDateTime: string
  checkinTime: string
  departureClicked: DepartureFunctions['DepartureClicked']
  departureId: string
  departurePortCode: string
  departurePortName?: string
  departureTime: string
  departureTimeFormatted?: string
  departureVisible: boolean
  isOpen: boolean
  loading: boolean
  onDepartureRowClicked: (departureId: string) => void
  onEditClicked: () => void
  returnLeg?: boolean
  selected?: boolean
  selectedType?: string
  tickets: BffInterfacesDeparture.Ticket[]
  tripDuration: string
}

const DepartureRowSingleTier = ({
  accordionOpen,
  active,
  arrivalDateTime,
  arrivalPortCode,
  arrivalPortName,
  arrivalTime,
  checkinDateTime,
  // checkinTime,
  departureClicked,
  departureId,
  departurePortCode,
  departurePortName,
  departureTime,
  departureTimeFormatted,
  departureVisible,
  isOpen,
  loading,
  onDepartureRowClicked,
  onEditClicked,
  returnLeg,
  selected,
  selectedType,
  tickets,
  tripDuration,
}: DepartureRowJerseyProps): React.ReactElement => {
  const { t } = useTranslation()
  const ticket = tickets[0]
  const soldOut = ticket?.soldOut
  const hidePriceAdjustment = ticket?.ticketOffer?.hidePriceAdjustment

  const selectedDepartures = useAtomValue(selectedDeparturesAtom)
  const isOverlapping = isDepartureOverlapping(returnLeg, selectedDepartures, checkinDateTime, arrivalDateTime)

  const departureRowRef: any = useRef(null)

  const isLoadingState = useAtomValue(loadingAtom)
  const setDeparturesBusy = useSetAtom(departuresBusyAtom)
  const [showOfferModal, setShowOfferModal] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState<BffInterfacesDeparture.Ticket | undefined>(undefined)
  const [selectionLoading, setSelectionLoading] = useState(false)
  const selectedOffer = useAtomValue(selectedOfferAtom)
  const selectedDates = useAtomValue(selectedDateAtom)
  const offerApplied: boolean =
    (!selectedOffer.offerRLSHours || (!!selectedOffer.offerRLSHours && !!selectedDates.returnDepartureDate)) &&
    tickets.filter((t) => t.ticketOffer && !t.ticketOffer.trackingOffer).length > 0

  const offerOnExtras: boolean = tickets.filter((t) => t.offerOnExtras).length > 0

  const cheapestTicket = tickets.reduce((cheapest, current) => {
    if (current?.soldOut) return cheapest
    const cheapestPrice = Math.min(cheapest.price, cheapest.ticketOffer?.price ?? cheapest.price)
    const currentPrice = Math.min(current.price, current.ticketOffer?.price ?? current.price)
    return currentPrice < cheapestPrice ? current : cheapest
  })

  function onSelectedTicket(ticket: BffInterfacesDeparture.Ticket) {
    if (returnLeg && selectedOffer.offerRLSHours && selectedDepartures.departureOut?.offer && !ticket.ticketOffer) {
      setSelectedTicket(ticket)
      setShowOfferModal(true)
      return
    } else {
      addDeparture(ticket)
    }
  }
  function onAccept() {
    if (selectedTicket) {
      addDeparture(selectedTicket)
      setShowOfferModal(false)
    }
  }

  const addDeparture = (ticket: BffInterfacesDeparture.Ticket) => {
    if (!loading) {
      setDeparturesBusy(true)
      setSelectionLoading(true)
      void Promise.resolve(departureClicked(departureId, !returnLeg ? 1 : 2, ticket.productCode)).finally(() => setSelectionLoading(false))
    }
  }

  const scrollToTargetAdjusted = useCallback(() => {
    setTimeout(() => {
      if (departureRowRef?.current) {
        const offsetTop = departureRowRef.current.getBoundingClientRect().top + window.pageYOffset - 16
        window.scrollTo({ behavior: 'smooth', top: offsetTop })
      }
    }, 200)
  }, [])

  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      scrollToTargetAdjusted()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      scrollToTargetAdjusted()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (selected && !isOpen) onDepartureRowClicked(departureId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  return (
    <>
      <li
        aria-label={`Departure from ${departurePortName} to ${arrivalPortName} at ${departureTimeFormatted}. Prices available from ${
          cheapestTicket.ticketOffer?.price ?? cheapestTicket.price
        } ${cheapestTicket.currency}.`}
        className={clsx(
          'mb-semantic-global-md md:mb-semantic-global-lg focus-visible:outline-semantic-global-primary-default z-50 flex-1 focus-visible:outline-2 focus-visible:outline-offset-2',
          (loading || soldOut) && 'cursor-not-allowed',
          isOpen && !isLoadingState.newDateLoading
            ? 'bg-[#EFF7FC]'
            : !isLoadingState.newDateLoading &&
                `bg-semantic-global-background-white ${!isMobile && !isTablet && 'hover:bg-dfds-listitem-hover'} cursor-pointer`
        )}
        data-e2e="departure-row"
        data-e2e-active={active}
        data-e2e-departure-time={departureTimeFormatted}
        data-e2e-has-offer={cheapestTicket.ticketOffer?.code !== undefined}
        data-e2e-loading={loading}
        data-e2e-overlapping={isOverlapping}
        data-e2e-route={`${departurePortCode}-${arrivalPortCode}`}
        data-e2e-selected={selected}
        data-e2e-standard={!soldOut}
        data-e2e-visible={departureVisible}
        id={departureId}
        onClick={() => {
          if (!soldOut && !selected && (!isOverlapping || !returnLeg)) {
            if (!isOverlapping) {
              onDepartureRowClicked(departureId)
            }
            onSelectedTicket(!soldOut && ticket)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === ' ') {
            e.preventDefault()
            if (!soldOut && !selected && (!isOverlapping || !returnLeg)) {
              if (!isOverlapping) {
                onDepartureRowClicked(departureId)
              }
              onSelectedTicket(!soldOut && ticket)
            }
          }
        }}
        ref={departureRowRef}
        role="listitem"
        tabIndex={0}
      >
        {isLoadingState.newDateLoading &&
        ((isLoadingState.legNumber === 1 && !returnLeg) || (isLoadingState.legNumber === 2 && returnLeg)) ? (
          <RowSkeleton />
        ) : (
          <div className="border-semantic-global-border-light flex flex-col border">
            {/* Mobile */}
            <div
              className={clsx(
                'py-primitive-16 px-semantic-global-md hover:bg-component-list-background-color-hover flex md:hidden',
                isOpen || selected
                  ? accordionOpen
                    ? 'bg-semantic-global-background-sand'
                    : 'bg-semantic-global-success-lightest'
                  : soldOut || isOverlapping
                    ? 'bg-semantic-global-disabled-light'
                    : 'bg-semantic-global-background-white'
              )}
            >
              <div className="gap-semantic-global-md relative flex flex-1 flex-col">
                <div className="gap-primitive-8 flex items-center justify-between">
                  <div className="gap-primitive-2 typography-body-xs-regular text-semantic-global-text-light flex flex-col">
                    {/* <div
                      className={clsx({
                        '': soldOut,
                        'text-semantic-form-controls-error-primary': isOverlapping,
                      })}
                    >
                      {t('CHECK-IN-CLOSES')}: {checkinTime}
                    </div> */}
                    <div
                      className={clsx({
                        'text-semantic-global-disabled-default': soldOut,
                      })}
                    >
                      {t('DURATION')}: {tripDuration}
                    </div>
                  </div>
                  {(offerOnExtras || offerApplied) && !soldOut && !isOverlapping ? (
                    <div className="bg-semantic-global-success-dark rounded-[12px]">
                      <div className="py-semantic-global-2xs px-semantic-global-xs w-fit" data-e2e="offer-badge">
                        <div aria-label="Offer Label" className="typography-body-xs-medium text-semantic-global-text-lightest">
                          {offerOnExtras && !offerApplied ? t('OFFER_ON_EXTRA_BADGE') : t('OFFER')}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {isOverlapping && !soldOut ? (
                    <div className="left-primitive-16 top-primitive-8 bg-semantic-form-controls-error-primary rounded-full">
                      <div className="py-semantic-global-2xs px-semantic-global-xs w-fit" data-e2e="overlapping-badge">
                        <div aria-label="Overlapping Label" className="typography-body-sm-medium text-semantic-global-text-lightest">
                          {t(`${!returnLeg ? 'RETURN' : 'OUTBOUND'}-OVERLAP`)}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div
                  className={clsx(
                    'gap-primitive-8 flex items-center justify-between',
                    (loading || soldOut || isOverlapping) && 'pointer-events-none cursor-wait'
                  )}
                >
                  <div className="flex flex-col">
                    <div
                      className={`typography-heading-md ${
                        soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-dfds-text-primary-primary'
                      }`}
                    >
                      {departureTime}
                    </div>
                    <div
                      className={`typography-body-md-regular whitespace-nowrap ${
                        soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-dfds-text-primary-primary'
                      }`}
                    >
                      {departurePortName}
                    </div>
                  </div>
                  <div className="gap-primitive-8 text-dfds-text-primary-primary flex w-full items-center justify-center">
                    <RouteIllustration />
                  </div>
                  <div className="flex flex-col items-end">
                    <div
                      className={`typography-heading-md ${
                        soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-dfds-text-primary-primary'
                      }`}
                    >
                      {arrivalTime}
                    </div>
                    <div
                      className={`typography-body-md-regular text-right whitespace-nowrap ${
                        soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-dfds-text-primary-primary'
                      }`}
                    >
                      {arrivalPortName}
                    </div>
                  </div>
                </div>

                {!isOpen ? (
                  !selected ? (
                    <div
                      className={clsx(
                        'gap-primitive-8 flex items-end justify-end',
                        (loading || soldOut || (isOverlapping && returnLeg)) && 'pointer-events-none cursor-wait'
                      )}
                    >
                      {!soldOut ? (
                        <>
                          {cheapestTicket.ticketOffer?.price ? (
                            <span className="typography-body-sm-regular text-semantic-global-text-light mb-0.5 line-through">
                              <Price currency={tickets[0].currency} dataAttr="departure-row-price" price={cheapestTicket.price} />
                            </span>
                          ) : null}
                          <span
                            aria-label="price"
                            className={clsx(
                              'typography-number-md',
                              soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                            )}
                            data-e2e="price"
                          >
                            <Price
                              currency={tickets[0].currency}
                              dataAttr={cheapestTicket.ticketOffer?.price ? 'departure-row-offer-price' : 'departure-row-price'}
                              price={cheapestTicket.ticketOffer?.price ?? cheapestTicket.price}
                            />
                          </span>
                        </>
                      ) : (
                        <div className="text-semantic-global-disabled-default typography-heading-md">{t('GENERAL-SOLD-OUT')}</div>
                      )}
                    </div>
                  ) : null
                ) : selectedType && !accordionOpen ? (
                  <div className="text-semantic-global-success-darker gap-semantic-global-xs relative flex h-full items-center justify-end">
                    <span className="typography-body-md-medium">
                      {t('STANDARD-TICKET')} {t('SELECTED')?.toLowerCase()}
                    </span>
                    <StatusSuccessFilledIcon size="xl" />
                  </div>
                ) : null}
              </div>
            </div>
            {/* Desktop */}
            <div
              className={clsx(
                'hover:bg-component-list-background-color-hover hidden h-full w-full flex-row md:flex',
                soldOut || isOverlapping ? 'bg-semantic-global-disabled-light' : 'bg-semantic-global-background-white'
              )}
              onClick={() => {
                if (selected && accordionOpen) {
                  onDepartureRowClicked(isOpen ? '' : departureId)
                }

                if (selected && !accordionOpen) {
                  onEditClicked()
                }
              }}
            >
              {/* Offer badge */}
              {offerApplied ? (
                <div
                  className="typography-body-md-medium bg-semantic-global-success-dark text-semantic-global-background-white py-semantic-global-md flex items-center justify-center [writing-mode:vertical-lr]"
                  data-e2e="desktop-offer-badge"
                >
                  {t('OFFER')}
                </div>
              ) : null}

              {offerOnExtras && !offerApplied && !soldOut && !isOverlapping ? (
                <div
                  className="typography-body-md-medium bg-semantic-global-success-dark text-semantic-global-background-white py-semantic-global-md flex items-center justify-center [writing-mode:vertical-lr]"
                  data-e2e="desktop-extras-offer-badge"
                >
                  {t('OFFER_ON_EXTRA_BADGE')}
                </div>
              ) : null}

              {/* Departure information */}
              <div className="relative flex flex-1 flex-col justify-center">
                {isOverlapping ? (
                  <div className="pt-semantic-global-md pl-semantic-global-lg flex-1">
                    <div className="w-fit" data-e2e="overlapping-badge">
                      <div className="typography-body-sm-regular px-primitive-8 text-semantic-global-text-lightest bg-semantic-form-controls-error-primary rounded-l-full rounded-r-full py-[2px]">
                        {t(`${!returnLeg ? 'RETURN' : 'OUTBOUND'}-OVERLAP`)}
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className={clsx('flex flex-1 flex-row', isOpen && accordionOpen && 'border-semantic-global-border-light border-b')}>
                  <div className="flex flex-1 items-center">
                    <div className="p-semantic-global-lg gap-semantic-global-sm flex flex-1 flex-row">
                      <div className="flex flex-col">
                        <div
                          className={`typography-heading-sm ${
                            soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                          }`}
                        >
                          {departureTime}
                        </div>
                        <div
                          className={clsx(
                            'typography-body-md-regular whitespace-nowrap',
                            soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                          )}
                        >
                          {departurePortName}
                        </div>
                      </div>
                      <div className="gap-primitive-8 text-semantic-global-text-default flex flex-1 items-center justify-center">
                        <RouteIllustration />
                      </div>
                      <div className="flex flex-col items-end">
                        <div
                          className={clsx(
                            'typography-heading-sm',
                            soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                          )}
                        >
                          {arrivalTime}
                        </div>
                        <div
                          className={clsx(
                            'typography-body-md-regular whitespace-nowrap',
                            soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                          )}
                        >
                          {arrivalPortName}
                        </div>
                      </div>
                    </div>

                    <div
                      className={clsx(
                        'p-semantic-global-lg typography-body-md-regular flex flex-col md:min-w-[250px] md:pl-0',
                        soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                      )}
                    >
                      {/* <div>
                        {t('CHECK-IN-CLOSES')} {checkinTime}
                      </div> */}
                      <div>{`${t('DURATION')}: ${tripDuration}`}</div>
                    </div>
                  </div>
                </div>
              </div>

              {accordionOpen ? (
                <div
                  className={clsx(
                    'border-semantic-global-border-light ml-auto w-[180px] border-l',
                    isOverlapping || soldOut ? '' : 'bg-semantic-global-background-sand'
                  )}
                >
                  <div className={clsx('p-semantic-global-lg relative flex h-full items-center justify-center')}>
                    {soldOut ? (
                      <div className="flex flex-1 flex-row items-center justify-center" data-e2e="departure-row-sold-out">
                        <div className="dfds-label text-semantic-global-disabled-default">{t('GENERAL-SOLD-OUT').toUpperCase()}</div>
                      </div>
                    ) : cheapestTicket.ticketOffer && offerApplied && !hidePriceAdjustment && !soldOut ? (
                      <div className="gap-primitive-4 flex flex-col">
                        <div className="gap-primitive-4 bg-semantic-global-success-dark p-semantic-global-2xs flex flex-col items-center">
                          <span
                            className={clsx(
                              'typography-heading-md',
                              !isOverlapping || !returnLeg ? 'text-semantic-global-background-white' : 'text-semantic-global-text-default'
                            )}
                            data-e2e="offer-price"
                          >
                            <Price
                              currency={cheapestTicket.currency}
                              dataAttr="departure-row-offer-price"
                              price={cheapestTicket.ticketOffer?.price ?? 0}
                            />
                          </span>
                        </div>
                        <div
                          className={clsx(
                            'typography-body-md-regular line-through',
                            isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-light'
                          )}
                          data-e2e="line-through-price"
                        >
                          <Price currency={cheapestTicket.currency} dataAttr="departure-row-price" price={cheapestTicket.price} />
                        </div>
                      </div>
                    ) : (
                      <div
                        className={clsx(
                          'gap-primitive-4 flex flex-col items-start',
                          !isOverlapping ? 'text-semantic-global-text-default' : 'text-semantic-global-disabled-default'
                        )}
                      >
                        <span
                          className={clsx(
                            'typography-heading-md',
                            soldOut || isOverlapping ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
                          )}
                          data-e2e="price"
                        >
                          <Price
                            currency={cheapestTicket.currency}
                            dataAttr="departure-row-price"
                            price={cheapestTicket.ticketOffer?.price ?? cheapestTicket.price}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-semantic-global-success-lightest border-semantic-global-border-light ml-auto min-w-[180px] border-l">
                  {selectedType ? (
                    <div className="p-semantic-global-lg gap-semantic-global-sm text-semantic-global-success-darker relative flex h-full items-center justify-center">
                      <span className="typography-body-md-medium">{t(`${selectedType}-TICKET`)}</span>
                      <StatusSuccessFilledIcon size="xl" />
                    </div>
                  ) : null}
                </div>
              )}

              {accordionOpen ? (
                <div
                  className={clsx(
                    'border-semantic-global-border-light px-semantic-global-md flex cursor-pointer items-center border-l',
                    isOpen && accordionOpen && 'border-semantic-global-border-light border-b'
                  )}
                >
                  {isOpen ? <ChevronUpIcon size="lg" /> : <ChevronDownIcon size="lg" />}
                </div>
              ) : null}
            </div>
            <Collapse
              className="bg-semantic-global-background-sand"
              data-e2e="departure-row-selections"
              data-e2e-active={active}
              data-e2e-departure-time={departureTimeFormatted}
              data-e2e-loading={loading}
              data-e2e-route={`${departurePortCode}-${arrivalPortCode}`}
              data-e2e-selected={selected}
              data-e2e-visible={departureVisible}
              id={`${departureId}-departureRowSelections`}
              isOpen={Boolean(isOpen && accordionOpen)}
            >
              <DepartureRowSelectionSingleTier
                addDeparture={onSelectedTicket}
                departureId={departureId}
                loading={loading}
                offerApplied={offerApplied}
                returnLeg={returnLeg ?? false}
                selected={selected}
                selectionLoading={selectionLoading}
                selectedType={selectedType}
                ticket={ticket}
              />
            </Collapse>
          </div>
        )}
      </li>
      <DepartureWithNoOfferModal
        offerTitle={selectedOffer?.offerTitle}
        onAccept={onAccept}
        onClose={() => setShowOfferModal(false)}
        show={showOfferModal}
      />
    </>
  )
}

export default memo(DepartureRowSingleTier)
